import type { Extension } from "@codemirror/state";
import {
  Decoration,
  type DecorationSet,
  EditorView,
  ViewPlugin,
  type ViewUpdate,
  WidgetType,
} from "@codemirror/view";
import type { DefaultTheme } from "styled-components";
import { emptySlotHintsIn } from "./emptySlotHints";

// The ghost hints for empty "" reference values (emptySlotHints.ts), drawn
// as widgets between the quotes — an input's placeholder, per field. Not
// document text: never saved, never selectable, gone on the first typed
// character. A click on a hint lands the cursor between the quotes (the
// widget lets events through); typing there brings up the suggestions the
// usual way (activate-on-typing) — nothing opens on the click itself.

class HintWidget extends WidgetType {
  constructor(private readonly hint: string) {
    super();
  }

  eq(other: HintWidget): boolean {
    return other.hint === this.hint;
  }

  toDOM(): HTMLElement {
    const span = document.createElement("span");
    span.className = "cm-slot-hint";
    span.textContent = this.hint;
    return span;
  }

  // Let the editor handle clicks on the hint so the cursor lands at the
  // widget's position — between the quotes.
  ignoreEvent(): boolean {
    return false;
  }
}

function hintDecorations(view: EditorView): DecorationSet {
  return Decoration.set(
    emptySlotHintsIn(view.state).map(({ pos, hint }) =>
      Decoration.widget({ widget: new HintWidget(hint), side: 1 }).range(pos),
    ),
  );
}

const hintPlugin = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;

    constructor(view: EditorView) {
      this.decorations = hintDecorations(view);
    }

    update(update: ViewUpdate): void {
      // The syntax tree can arrive after the doc change (parse work is
      // incremental), so re-read on any doc or viewport change.
      if (update.docChanged || update.viewportChanged) {
        this.decorations = hintDecorations(update.view);
      }
    }
  },
  { decorations: (plugin) => plugin.decorations },
);

/** Ghost hints in empty reference values, plus their look: the editor's
 *  placeholder gray with a dotted underline standing in for the lint
 *  underline the placeholder warning gives up (pipelineLint's
 *  diagnosticFor tags those with cm-slot-warning); the gutter mark and
 *  tooltip stay. */
export function emptySlotHints(theme: DefaultTheme): Extension {
  return [
    hintPlugin,
    EditorView.theme({
      ".cm-slot-hint": {
        color: theme.editorLineNumTxt,
        borderBottom: "1px dotted currentColor",
        pointerEvents: "none",
      },
      // The ghost marks the slot; the wavy line would only bracket it.
      ".cm-slot-warning": { backgroundImage: "none" },
    }),
  ];
}
